import React, { useState, useEffect } from 'react';
import { Plus, Search, Phone, MessageSquare, FileText, X, Users } from 'lucide-react';
import { Customer, getCustomers } from '../../lib/customers';
import { useBusiness } from '../../contexts/BusinessContext';
import CustomerModal from '../CustomerModal';

interface SelectCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (customer: Customer) => void;
}

export default function SelectCustomerModal({
  isOpen,
  onClose,
  onSelect
}: SelectCustomerModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCustomers = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getCustomers(selectedBusiness.id);
      setCustomers(data);
      setError(null);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadCustomers();
    }
  }, [isOpen, selectedBusiness?.id]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setError(null);
    }
  };

  const handleCustomerCreated = async () => {
    setIsCustomerModalOpen(false);
    await loadCustomers();

    // Get fresh customer list and select the most recent one
    const freshCustomers = await getCustomers(selectedBusiness?.id || '');
    const newCustomer = freshCustomers[0];

    if (newCustomer) {
      onSelect(newCustomer);
    }
  };

  const filteredCustomers = searchQuery ? customers.filter(customer => {
    const searchLower = searchQuery.toLowerCase();
    return (
      `${customer.first_name} ${customer.last_name}`.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower) ||
      customer.phone?.includes(searchLower)
    );
  }) : customers;

  if (!isOpen && !isAnimating) return null;

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
        } z-[100000]`}
        onClick={onClose}
        style={{
          zIndex: 100000,
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
          isolation: 'isolate'
        }}
      >
        <div 
          className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          onTransitionEnd={handleTransitionEnd}
          onClick={e => e.stopPropagation()}
        >
          <div className="h-full flex flex-col">
            <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Select Customer</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setIsCustomerModalOpen(true)}
                className="ml-4 inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Customer
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-600">Loading customers...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="p-6">
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                    {error}
                  </div>
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div className="text-center py-6">
                  <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="h-6 w-6 text-gray-400" />
                  </div>
                  <h3 className="text-sm font-medium text-gray-900">No customers found</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    {searchQuery ? 'Try adjusting your search terms' : 'Get started by adding your first customer'}
                  </p>
                  {!searchQuery && (
                    <button
                      onClick={() => setIsCustomerModalOpen(true)}
                      className="mt-4 inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Customer
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => onSelect(customer)}
                      className="bg-white rounded-lg border border-gray-200 p-4 hover:bg-gray-50 hover:border-gray-300 cursor-pointer transition-all duration-200"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="h-10 w-10 rounded-full bg-black flex items-center justify-center text-sm font-medium text-white">
                            {customer.first_name?.[0]}{customer.last_name?.[0]}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {customer.first_name || ''} {customer.last_name || ''}
                            </div>
                            <div className="text-sm text-gray-500">
                              {customer.email || ''}
                            </div>
                            {customer.phone && (
                              <div className="text-sm text-gray-500">
                                {customer.phone}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button 
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (customer.phone) {
                                window.open(`tel:${customer.phone}`);
                              }
                            }}
                          >
                            <Phone className="h-4 w-4" />
                          </button>
                          <button 
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle message
                            }}
                          >
                            <MessageSquare className="h-4 w-4" />
                          </button>
                          <button 
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle document
                            }}
                          >
                            <FileText className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <CustomerModal
        isOpen={isCustomerModalOpen}
        onClose={() => setIsCustomerModalOpen(false)}
        onSuccess={handleCustomerCreated}
        businessId={selectedBusiness?.id}
      />
    </>
  );
}